import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Title,
  Text,
  Stack,
  Button,
  Select,
  Textarea,
  Paper,
  Group,
  Modal,
  Alert,
  Badge,
  Loader,
  Divider,
  ActionIcon,
  Tooltip,
  Progress,
  ThemeIcon
} from '@mantine/core';
import {
  IconMicrophone,
  IconDownload,
  IconAlertCircle,
  IconInfoCircle,
  IconVolume,
  IconPlayerPlay,
  IconPlayerPause,
  IconLanguage,
  IconWand,
} from '@tabler/icons-react';
import { useAuth } from '../../shared/contexts/AuthContext';
import PlatformNavBar from '../../components/PlatformNavBar';

const API_BASE = process.env.REACT_APP_CCUTILS_API_BASE_URL || '';

// Polly-supported language codes with display names
const LANGUAGES = [
  { value: 'arb',   label: 'Arabic (Modern Standard)' },
  { value: 'ar-AE', label: 'Arabic (Gulf)' },
  { value: 'ca-ES', label: 'Catalan' },
  { value: 'yue-CN', label: 'Chinese (Cantonese)' },
  { value: 'cmn-CN', label: 'Chinese (Mandarin)' },
  { value: 'da-DK', label: 'Danish' },
  { value: 'nl-NL', label: 'Dutch (Netherlands)' },
  { value: 'nl-BE', label: 'Dutch (Belgium)' },
  { value: 'en-AU', label: 'English (Australian)' },
  { value: 'en-GB', label: 'English (British)' },
  { value: 'en-IN', label: 'English (Indian)' },
  { value: 'en-NZ', label: 'English (New Zealand)' },
  { value: 'en-ZA', label: 'English (South African)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB-WLS', label: 'English (Welsh)' },
  { value: 'fi-FI', label: 'Finnish' },
  { value: 'fr-FR', label: 'French (France)' },
  { value: 'fr-BE', label: 'French (Belgium)' },
  { value: 'fr-CA', label: 'French (Canada)' },
  { value: 'de-DE', label: 'German' },
  { value: 'de-AT', label: 'German (Austria)' },
  { value: 'de-CH', label: 'German (Switzerland)' },
  { value: 'hi-IN', label: 'Hindi' },
  { value: 'is-IS', label: 'Icelandic' },
  { value: 'it-IT', label: 'Italian' },
  { value: 'ja-JP', label: 'Japanese' },
  { value: 'ko-KR', label: 'Korean' },
  { value: 'nb-NO', label: 'Norwegian' },
  { value: 'pl-PL', label: 'Polish' },
  { value: 'pt-BR', label: 'Portuguese (Brazil)' },
  { value: 'pt-PT', label: 'Portuguese (Portugal)' },
  { value: 'ro-RO', label: 'Romanian' },
  { value: 'ru-RU', label: 'Russian' },
  { value: 'es-ES', label: 'Spanish (Castilian)' },
  { value: 'es-MX', label: 'Spanish (Mexico)' },
  { value: 'es-US', label: 'Spanish (US)' },
  { value: 'sv-SE', label: 'Swedish' },
  { value: 'tr-TR', label: 'Turkish' },
  { value: 'vi-VN', label: 'Vietnamese' },
  { value: 'cy-GB', label: 'Welsh' },
];

const ENGINE_OPTIONS = [
  { value: 'neural',     label: 'Neural (Recommended)' },
  { value: 'standard',   label: 'Standard' },
  { value: 'long-form',  label: 'Long-Form' },
  { value: 'generative', label: 'Generative' },
];

function base64ToBlob(base64, mimeType) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}

function AudioPlayer({ audioUrl }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
      }
    };
  }, []);

  const formatTime = (secs) => {
    if (!isFinite(secs) || isNaN(secs)) return '0:00';
    const m = Math.floor(secs / 60);
    const s = Math.floor(secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
    if (audioRef.current.duration) {
      setProgress((audioRef.current.currentTime / audioRef.current.duration) * 100);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) setDuration(audioRef.current.duration);
  };

  const handleEnded = () => {
    setPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    if (audioRef.current) audioRef.current.currentTime = 0;
  };

  const handleProgressClick = (e) => {
    if (!audioRef.current || !audioRef.current.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    audioRef.current.currentTime = ratio * audioRef.current.duration;
  };

  return (
    <Box>
      <audio
        ref={audioRef}
        src={audioUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        preload="auto"
      />
      <Paper withBorder radius="md" p="md" style={{ background: 'var(--mantine-color-teal-0)' }}>
        <Group gap="md" align="center">
          <ActionIcon
            size="xl"
            radius="xl"
            color="teal"
            variant="filled"
            onClick={togglePlay}
          >
            {playing ? <IconPlayerPause size={22} /> : <IconPlayerPlay size={22} />}
          </ActionIcon>
          <Stack gap={4} style={{ flex: 1 }}>
            <Box
              style={{ cursor: 'pointer' }}
              onClick={handleProgressClick}
            >
              <Progress
                value={progress}
                color="teal"
                size="md"
                radius="xl"
              />
            </Box>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">{formatTime(currentTime)}</Text>
              <Text size="xs" c="dimmed">{formatTime(duration)}</Text>
            </Group>
          </Stack>
          <ThemeIcon size="lg" radius="xl" color="teal" variant="light">
            <IconVolume size={18} />
          </ThemeIcon>
        </Group>
      </Paper>
    </Box>
  );
}

function AudioFileGenerator({ onBackToPlatformSelector, onBackToHome }) {
  const { getApiToken } = useAuth();
  const [text, setText] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState(null);
  const [selectedEngine, setSelectedEngine] = useState('neural');
  const [loadingVoices, setLoadingVoices] = useState(false);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [voiceError, setVoiceError] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [audioResult, setAudioResult] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);

  // Fetch voices when language changes
  useEffect(() => {
    if (!selectedLanguage) {
      setVoices([]);
      setSelectedVoice(null);
      return;
    }

    const fetchVoices = async () => {
      setLoadingVoices(true);
      setVoiceError(null);
      setSelectedVoice(null);
      setVoices([]);

      try {
        const url = `${API_BASE}/ccutils/audio/list-voices?language_code=${encodeURIComponent(selectedLanguage)}`;
        const headers = {};
        try {
          const token = await getApiToken();
          if (token) headers['Authorization'] = `Bearer ${token}`;
        } catch (e) { console.warn('Failed to get API token:', e); }
        const res = await fetch(url, { headers });
        const data = await res.json();

        if (!res.ok) {
          throw new Error(data.error || `Failed to load voices (${res.status})`);
        }

        const voiceOptions = data.voices.map((v) => ({
          value: v.voice_id,
          label: `${v.name} (${v.gender}) — ${v.language_name}`,
          engines: v.engines,
          gender: v.gender,
        }));
        setVoices(voiceOptions);
      } catch (err) {
        setVoiceError(err.message);
      } finally {
        setLoadingVoices(false);
      }
    };

    fetchVoices();
  }, [selectedLanguage]);

  // Determine supported engines for the selected voice
  const selectedVoiceData = voices.find((v) => v.value === selectedVoice);
  const supportedEngines = selectedVoiceData?.engines || [];
  const engineOptions = ENGINE_OPTIONS.filter(
    (e) => supportedEngines.length === 0 || supportedEngines.includes(e.value)
  );

  // If the current engine isn't supported by the new voice, switch to first available
  useEffect(() => {
    if (supportedEngines.length > 0 && !supportedEngines.includes(selectedEngine)) {
      // Prefer neural, else first available
      if (supportedEngines.includes('neural')) {
        setSelectedEngine('neural');
      } else {
        setSelectedEngine(supportedEngines[0]);
      }
    }
  }, [selectedVoice, supportedEngines, selectedEngine]);

  const handleSubmit = async () => {
    if (!text.trim() || !selectedLanguage || !selectedVoice || !selectedEngine) return;

    setLoadingGenerate(true);
    setGenerateError(null);

    // Clean up previous audio URL
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }

    try {
      const generateHeaders = { 'Content-Type': 'application/json' };
      try {
        const token = await getApiToken();
        if (token) generateHeaders['Authorization'] = `Bearer ${token}`;
      } catch (e) { console.warn('Failed to get API token:', e); }
      const res = await fetch(`${API_BASE}/ccutils/audio/generate`, {
        method: 'POST',
        headers: generateHeaders,
        body: JSON.stringify({
          language_code: selectedLanguage,
          text: text.trim(),
          voice_id: selectedVoice,
          engine: selectedEngine,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || `Generation failed (${res.status})`);
      }

      const blob = base64ToBlob(data.audio_base64, 'audio/wav');
      const url = URL.createObjectURL(blob);

      setAudioResult(data);
      setAudioUrl(url);
      setModalOpen(true);
    } catch (err) {
      setGenerateError(err.message);
    } finally {
      setLoadingGenerate(false);
    }
  };

  const handleDownload = () => {
    if (!audioUrl || !audioResult) return;
    const a = document.createElement('a');
    a.href = audioUrl;
    a.download = audioResult.filename;
    a.click();
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const isFormValid = text.trim().length > 0 && selectedLanguage && selectedVoice && selectedEngine;
  const charCount = text.length;
  const MAX_CHARS = 3000;

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0ca678 0%, #099268 100%)',
        padding: '40px 20px',
      }}
    >
      <Container size="md">
        {/* Top-left navigation: exit to platform selector + return to the CC Utils hub */}
        <PlatformNavBar
          onBackToPlatformSelector={onBackToPlatformSelector}
          onBackToHome={onBackToHome}
          showHome={!!onBackToHome}
          homeLabel="Contact Center Utilities"
          onGradient
          mb="lg"
        />

        {/* Header */}
        <Stack align="center" gap="xs" mb="xl">
          <ThemeIcon size={64} radius="xl" color="white" variant="filled"
            style={{ background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)' }}>
            <IconMicrophone size={36} style={{ color: 'white' }} />
          </ThemeIcon>
          <Title
            order={1}
            size="2.2rem"
            fw={700}
            style={{ color: 'white', textAlign: 'center' }}
          >
            Audio File Generator
          </Title>
          <Text size="md" style={{ color: 'rgba(255,255,255,0.8)', textAlign: 'center', maxWidth: 500 }}>
            Convert text to μ-law WAV audio using Amazon Polly with automatic translation support.
          </Text>
        </Stack>

        {/* Main form card */}
        <Paper shadow="xl" radius="lg" p="xl" style={{ background: 'white' }}>
          <Stack gap="lg">

            {/* API config warning */}
            {!API_BASE && (
              <Alert icon={<IconAlertCircle size={16} />} color="orange" variant="light" radius="md">
                <Text size="sm">
                  <strong>API not configured.</strong> Set <code>REACT_APP_CCUTILS_API_BASE_URL</code> in your environment to enable audio generation.
                </Text>
              </Alert>
            )}

            {/* Text input */}
            <Box>
              <Group justify="space-between" mb={6}>
                <Text size="sm" fw={600} c="dark">
                  Text to Synthesize
                </Text>
                <Text
                  size="xs"
                  c={charCount > MAX_CHARS ? 'red' : 'dimmed'}
                >
                  {charCount.toLocaleString()} / {MAX_CHARS.toLocaleString()}
                </Text>
              </Group>
              <Textarea
                placeholder="Enter the text you want to convert to audio..."
                value={text}
                onChange={(e) => setText(e.currentTarget.value)}
                minRows={4}
                maxRows={8}
                autosize
                radius="md"
                styles={{
                  input: {
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                  },
                }}
              />
              {charCount > MAX_CHARS && (
                <Text size="xs" c="red" mt={4}>
                  Text exceeds the {MAX_CHARS.toLocaleString()} character limit.
                </Text>
              )}
            </Box>

            <Divider />

            {/* Language + Voice row */}
            <Group grow align="flex-start" gap="md">
              {/* Language */}
              <Box>
                <Group gap={6} mb={6}>
                  <IconLanguage size={16} color="var(--mantine-color-teal-6)" />
                  <Text size="sm" fw={600} c="dark">Language</Text>
                </Group>
                <Select
                  placeholder="Select a language..."
                  data={LANGUAGES}
                  value={selectedLanguage}
                  onChange={(val) => {
                    setSelectedLanguage(val);
                    setGenerateError(null);
                  }}
                  searchable
                  clearable
                  radius="md"
                  maxDropdownHeight={280}
                  nothingFoundMessage="No matching languages"
                />
              </Box>

              {/* Voice */}
              <Box>
                <Group gap={6} mb={6}>
                  <IconMicrophone size={16} color="var(--mantine-color-teal-6)" />
                  <Text size="sm" fw={600} c="dark">Voice</Text>
                  {loadingVoices && <Loader size="xs" color="teal" />}
                </Group>
                <Select
                  placeholder={
                    !selectedLanguage
                      ? 'Select a language first'
                      : loadingVoices
                      ? 'Loading voices...'
                      : voices.length === 0
                      ? 'No voices available'
                      : 'Select a voice...'
                  }
                  data={voices}
                  value={selectedVoice}
                  onChange={(val) => {
                    setSelectedVoice(val);
                    setGenerateError(null);
                  }}
                  disabled={!selectedLanguage || loadingVoices || voices.length === 0}
                  searchable
                  clearable
                  radius="md"
                  maxDropdownHeight={280}
                  nothingFoundMessage="No matching voices"
                />
                {voiceError && (
                  <Text size="xs" c="red" mt={4}>
                    {voiceError}
                  </Text>
                )}
              </Box>
            </Group>

            {/* Engine */}
            <Box>
              <Group gap={6} mb={6}>
                <IconWand size={16} color="var(--mantine-color-teal-6)" />
                <Text size="sm" fw={600} c="dark">Engine</Text>
                {selectedVoice && supportedEngines.length > 0 && (
                  <Tooltip label="Only engines supported by the selected voice are shown" withArrow>
                    <IconInfoCircle size={14} color="var(--mantine-color-dimmed)" style={{ cursor: 'help' }} />
                  </Tooltip>
                )}
              </Group>
              <Select
                data={selectedVoice ? engineOptions : ENGINE_OPTIONS}
                value={selectedEngine}
                onChange={setSelectedEngine}
                disabled={!selectedVoice}
                radius="md"
                maxDropdownHeight={200}
              />
              {selectedVoice && engineOptions.length === 0 && (
                <Text size="xs" c="orange" mt={4}>
                  No engine data available for this voice — defaulting to standard.
                </Text>
              )}
            </Box>

            {/* Translation note */}
            <Alert icon={<IconInfoCircle size={16} />} color="teal" variant="light" radius="md">
              <Text size="sm">
                Text is automatically translated to the selected language before synthesis using Amazon Translate.
                If the text is already in the target language, no translation is applied.
              </Text>
            </Alert>

            {/* Generate error */}
            {generateError && (
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light" radius="md">
                <Text size="sm">{generateError}</Text>
              </Alert>
            )}

            {/* Submit */}
            <Button
              size="lg"
              color="teal"
              radius="md"
              leftSection={loadingGenerate ? <Loader size="xs" color="white" /> : <IconMicrophone size={20} />}
              disabled={!isFormValid || loadingGenerate || charCount > MAX_CHARS || !API_BASE}
              onClick={handleSubmit}
              style={{ fontWeight: 600 }}
            >
              {loadingGenerate ? 'Generating Audio...' : 'Generate Audio'}
            </Button>

          </Stack>
        </Paper>

        {/* Footer note */}
        <Text size="xs" style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginTop: 24 }}>
          Powered by Amazon Polly &amp; Amazon Translate · μ-law WAV (8 kHz, Mono)
        </Text>
      </Container>

      {/* Audio result modal */}
      <Modal
        opened={modalOpen}
        onClose={handleCloseModal}
        title={
          <Group gap="sm">
            <ThemeIcon size="md" radius="md" color="teal" variant="light">
              <IconVolume size={16} />
            </ThemeIcon>
            <Text fw={600} size="md">Audio Preview</Text>
          </Group>
        }
        size="md"
        radius="lg"
        centered
        overlayProps={{ blur: 4, backgroundOpacity: 0.4 }}
      >
        {audioResult && audioUrl && (
          <Stack gap="lg">

            {/* Voice / language info */}
            <Paper withBorder radius="md" p="md" style={{ background: 'var(--mantine-color-gray-0)' }}>
              <Group gap="xs" wrap="wrap">
                <Badge color="teal" variant="light" size="sm">
                  {audioResult.voice_id}
                </Badge>
                <Badge color="blue" variant="light" size="sm">
                  {LANGUAGES.find(l => l.value === audioResult.language_code)?.label || audioResult.language_code}
                </Badge>
                <Badge color="violet" variant="light" size="sm">
                  {audioResult.format}
                </Badge>
                <Badge color="gray" variant="light" size="sm">
                  {audioResult.sample_rate} Hz
                </Badge>
                {audioResult.translation_applied && (
                  <Badge color="orange" variant="light" size="sm" leftSection={<IconLanguage size={10} />}>
                    Translated
                  </Badge>
                )}
              </Group>
            </Paper>

            {/* Translation result */}
            {audioResult.translation_applied && (
              <Box>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={4}>
                  Translated Text
                </Text>
                <Paper withBorder radius="md" p="sm" style={{ background: 'var(--mantine-color-orange-0)' }}>
                  <Text size="sm" style={{ fontStyle: 'italic', lineHeight: 1.5 }}>
                    "{audioResult.translated_text}"
                  </Text>
                </Paper>
              </Box>
            )}

            {/* Audio player */}
            <Box>
              <Text size="xs" fw={600} c="dimmed" tt="uppercase" mb={8}>
                Preview
              </Text>
              <AudioPlayer audioUrl={audioUrl} />
            </Box>

            <Divider />

            {/* Actions */}
            <Group justify="space-between">
              <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>
                {audioResult.filename}
              </Text>
              <Group gap="sm">
                <Button
                  variant="subtle"
                  color="gray"
                  size="sm"
                  radius="md"
                  onClick={handleCloseModal}
                >
                  Close
                </Button>
                <Button
                  color="teal"
                  size="sm"
                  radius="md"
                  leftSection={<IconDownload size={16} />}
                  onClick={handleDownload}
                >
                  Download WAV
                </Button>
              </Group>
            </Group>

          </Stack>
        )}
      </Modal>
    </Box>
  );
}

export default AudioFileGenerator;
